import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Package, DollarSign, Hash, Tag, Box, Percent, Star, Calendar,
  Camera, Upload, Barcode, X, Save, Ban, Edit3, Plus, Layers
} from 'lucide-react';
import { chainSummaryText } from '../../utils/formatStock';

const categoryList = [
  'Pan Masala', 'Gutka', 'Supari', 'Mouth Freshener', 'Cigarettes', 'Beverages', 'Snacks', 'Other',
];

const emptyLevel = () => ({ name: '', quantity: '', sellable: true, salePrice: '', purchasePrice: '', pluralName: '', contains: '' });

export default function ProductForm({ product, onSubmit, onCancel, loading = false }) {
  const isEdit = !!product;

  const [name, setName] = useState(product?.productName || product?.name || '');
  const [category, setCategory] = useState(product?.category || '');
  const [barcode, setBarcode] = useState(product?.barcode || '');
  const [taxRate, setTaxRate] = useState(product?.taxRate ?? '');
  const [purchasePrice, setPurchasePrice] = useState(product?.purchasePrice ?? '');
  const [salePrice, setSalePrice] = useState(product?.salePrice ?? '');
  const [minStock, setMinStock] = useState(product?.minimumStock ?? product?.minStock ?? '');
  const [stockQty, setStockQty] = useState(isEdit ? '' : (product?.currentStock ?? product?.stock ?? product?.quantity ?? ''));
  const [stockUnit, setStockUnit] = useState('0');
  const [expiryDate, setExpiryDate] = useState(product?.expiryDate ? product.expiryDate.substring(0, 10) : '');
  const [supplierName, setSupplierName] = useState(product?.supplierName || '');
  const [supplierContact, setSupplierContact] = useState(product?.supplierContact || product?.supplierPhone || '');
  const existingImageUrl = product?.imageUrl || product?.image || null;
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(existingImageUrl);
  const [errors, setErrors] = useState({});

  const deriveInitialLevels = useCallback(() => {
    const stored = product?.packaging?.units;
    if (Array.isArray(stored) && stored.length > 1) {
      return stored.slice(1).map((u) => ({
        name: u.name || '',
        quantity: u.quantity || 1,
        sellable: !!u.sellable,
        salePrice: u.salePrice ?? '',
        purchasePrice: u.purchasePrice ?? '',
        pluralName: u.pluralName || '',
        contains: u.lowerUnitName || '',
      }));
    }
    if (product?.piecesPerPack > 1 || product?.packsPerBox > 1) {
      const levels = [];
      if (product.piecesPerPack > 1) {
        levels.push({
          name: 'Pack',
          quantity: product.piecesPerPack,
          sellable: true,
          salePrice: product.pricePerFullPack ?? '',
          purchasePrice: '',
          pluralName: 'Packs',
          contains: '',
        });
      }
      if (product.packsPerBox > 1) {
        levels.push({
          name: 'Box',
          quantity: product.packsPerBox,
          sellable: true,
          salePrice: product.pricePerBox ?? '',
          purchasePrice: '',
          pluralName: 'Boxes',
          contains: '',
        });
      }
      return levels;
    }
    return [];
  }, [product]);

  const [packagingMode, setPackagingMode] = useState(
    product?.packaging?.mode === 'MULTI' || (product?.piecesPerPack || 1) > 1 || (product?.packsPerBox || 1) > 1
      ? 'MULTI'
      : 'SINGLE',
  );
  const [baseUnitName, setBaseUnitName] = useState(product?.packaging?.baseUnitName || 'Piece');
  const [levels, setLevels] = useState(deriveInitialLevels);

  const updateLevel = (index, field, value) => {
    setLevels((prev) => {
      const next = prev.map((lv, i) => (i === index ? { ...lv, [field]: value } : lv));
      if (field === 'name') {
        const oldName = (prev[index]?.name || '').trim();
        if (oldName && oldName !== String(value).trim()) {
          next.forEach((lv, i) => {
            if (i !== index && lv.contains && String(lv.contains).toLowerCase() === oldName.toLowerCase()) {
              next[i] = { ...lv, contains: String(value).trim() };
            }
          });
        }
      }
      return next;
    });
  };
  const addLevel = () => setLevels((prev) => {
    const previousName = prev.reduce((acc, lv) => (lv.name?.trim() ? lv.name.trim() : acc), baseUnitName.trim() || 'Piece');
    return [...prev, { ...emptyLevel(), contains: previousName }];
  });
  const removeLevel = (index) => setLevels((prev) => prev.filter((_, i) => i !== index));

  const units = useMemo(() => {
    const base = baseUnitName.trim() || 'Piece';
    const out = [{
      name: base,
      pluralName: /s$|x$|z$|ch$|sh$/i.test(base) ? `${base}es` : `${base}s`,
      quantity: 1,
      baseFactor: 1,
      lowerUnitName: null,
      sellable: true,
      salePrice: Number(salePrice) || 0,
      purchasePrice: Number(purchasePrice) || 0,
    }];
    levels.forEach((lv) => {
      if (!lv.name?.trim()) return;
      const qty = Math.max(1, Math.floor(Number(lv.quantity) || 1));
      const lname = lv.name.trim();
      const containerName = String(lv.contains || '').trim();
      const container = containerName
        ? out.find((u) => String(u.name).toLowerCase() === String(containerName).toLowerCase())
        : out[out.length - 1];
      const resolvedContainer = container || out[out.length - 1];
      out.push({
        name: lname,
        pluralName: lv.pluralName?.trim() || (/s$|x$|z$|ch$|sh$/i.test(lname) ? `${lname}es` : `${lname}s`),
        quantity: qty,
        baseFactor: qty * (resolvedContainer.baseFactor || 1),
        lowerUnitName: resolvedContainer.name,
        sellable: !!lv.sellable,
        salePrice: Number(lv.salePrice) || 0,
        purchasePrice: Number(lv.purchasePrice) || 0,
      });
    });
    return out;
  }, [baseUnitName, salePrice, purchasePrice, levels]);

  const summaryLines = useMemo(() => (packagingMode === 'MULTI' && units.length > 1 ? chainSummaryText(units) : []), [packagingMode, units]);

  const containerOptions = useMemo(() => {
    const base = baseUnitName.trim() || 'Piece';
    const options = [{ name: base, plural: /s$|x$|z$|ch$|sh$/i.test(base) ? `${base}es` : `${base}s` }];
    const seen = new Set([base.toLowerCase()]);
    levels.forEach((lv) => {
      if (!lv.name?.trim()) return;
      const n = lv.name.trim();
      if (seen.has(n.toLowerCase())) return;
      seen.add(n.toLowerCase());
      options.push({ name: n, plural: lv.pluralName?.trim() || (/s$|x$|z$|ch$|sh$/i.test(n) ? `${n}es` : `${n}s`) });
    });
    return options;
  }, [baseUnitName, levels]);

  const initialStockPreview = useMemo(() => {
    if (isEdit) return null;
    const unitIdx = Number(stockUnit);
    const qty = Number(stockQty);
    if (unitIdx <= 0 || !qty || qty <= 0 || !units[unitIdx]) return null;
    const base = units[unitIdx].baseFactor * qty;
    const unitPlural = units[unitIdx].pluralName || `${units[unitIdx].name}s`;
    const basePlural = units[0].pluralName || `${units[0].name}s`;
    return `${qty} ${units[unitIdx].name}${qty !== 1 ? ` (${unitPlural})` : ''} = ${base.toLocaleString()} ${units[0].name}${base !== 1 ? `s (${basePlural})` : ''}`;
  }, [isEdit, stockUnit, stockQty, units]);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Product name is required';
    if (!salePrice || Number(salePrice) <= 0) errs.salePrice = 'Valid base sale price is required';
    if (!isEdit && (stockQty === '' || Number(stockQty) < 0)) errs.stockQty = 'Valid stock quantity is required';
    if (packagingMode === 'MULTI' && units.length <= 1) errs.packaging = 'Add at least one higher packaging level';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      productName: name.trim(),
      name: name.trim(),
      category,
      barcode,
      taxRate: Number(taxRate) || 0,
      purchasePrice: Number(purchasePrice) || 0,
      salePrice: Number(salePrice) || 0,
      minimumStock: Number(minStock) || 0,
      minStock: Number(minStock) || 0,
      expiryDate: expiryDate || null,
      supplierName,
      supplierContact,
    };

    if (packagingMode === 'MULTI') {
      payload.packaging = {
        mode: 'MULTI',
        baseUnitName: baseUnitName.trim() || 'Piece',
        units: levels
          .filter((lv) => lv.name?.trim())
          .map((lv) => ({
            name: lv.name.trim(),
            pluralName: lv.pluralName?.trim() || undefined,
            quantity: Math.max(1, Math.floor(Number(lv.quantity) || 1)),
            contains: lv.contains?.trim() || undefined,
            sellable: !!lv.sellable,
            salePrice: Number(lv.salePrice) || 0,
            purchasePrice: Number(lv.purchasePrice) || 0,
          })),
      };
    } else {
      payload.packaging = {
        mode: 'SINGLE',
        baseUnitName: baseUnitName.trim() || 'Piece',
        units: [],
      };
    }

    if (!isEdit) {
      payload.initialStock = { unit: units[Number(stockUnit)]?.name || baseUnitName.trim() || 'Piece', quantity: Number(stockQty) || 0 };
    }

    // The image is a File: it cannot travel in the JSON body, so it is
    // handed to the caller separately and uploaded afterwards.
    onSubmit?.(payload, image);
  };

  const inputClass = (field) => `
    w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border rounded-lg
    text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400
    transition-colors
    ${errors[field] ? 'border-red-500 dark:border-red-400' : 'border-gray-200 dark:border-gray-600'}
  `;

  const plainInputClass = () => `
    w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border rounded-lg
    text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400
    transition-colors border-gray-200 dark:border-gray-600
  `;

  const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          {isEdit ? <Edit3 size={20} /> : <Package size={20} />}
          {isEdit ? 'Edit Product' : 'Add Product'}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* image upload */}
        <div className="space-y-4 md:col-span-2">
          <label className={labelClass}>Product Image</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <Camera size={24} className="text-gray-400" />
              )}
            </div>
            <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
              <Upload size={15} />
              Upload Image
              <input type="file" accept="image/*" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImage(file);
                  const reader = new FileReader();
                  reader.onload = (ev) => setImagePreview(ev.target.result);
                  reader.readAsDataURL(file);
                }
              }} className="hidden" />
            </label>
            {imagePreview && (
              <button type="button" onClick={() => { setImage(null); setImagePreview(null); }} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* product name */}
        <div>
          <label className={labelClass}>Product Name *</label>
          <div className="relative">
            <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" className={inputClass('name')} />
          </div>
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        {/* category */}
        <div>
          <label className={labelClass}>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500">
            <option value="">Select category</option>
            {categoryList.map((cat) => (<option key={cat} value={cat}>{cat}</option>))}
          </select>
        </div>

        {/* barcode */}
        <div>
          <label className={labelClass}>Barcode</label>
          <div className="relative">
            <Barcode size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="Enter barcode" className={inputClass('barcode')} />
          </div>
        </div>

        {/* tax */}
        <div>
          <label className={labelClass}>Tax Rate (%)</label>
          <div className="relative">
            <Percent size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" step="0.01" min="0" max="100" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="0" className={inputClass('taxRate')} />
          </div>
        </div>

        {/* prices */}
        <div>
          <label className={labelClass}>Purchase Price per {baseUnitName || 'Piece'}</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" step="0.01" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} placeholder="0.00" className={inputClass('purchasePrice')} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Price per {baseUnitName || 'Piece'} *</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" step="0.01" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="0.00" className={inputClass('salePrice')} />
          </div>
          {errors.salePrice && <p className="mt-1 text-xs text-red-500">{errors.salePrice}</p>}
        </div>

        {/* ─── Packaging Builder ────────────────────────────────────────────── */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Layers size={15} className="text-primary-600" />
              Packaging
            </h3>
            <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              {['SINGLE', 'MULTI'].map((m) => (
                <button key={m} type="button" onClick={() => setPackagingMode(m)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${packagingMode === m ? 'bg-white dark:bg-gray-800 shadow-sm text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                  {m === 'SINGLE' ? 'Single Unit' : 'Multiple Levels'}
                </button>
              ))}
            </div>
          </div>

          {errors.packaging && <p className="mb-2 text-xs text-red-500">{errors.packaging}</p>}

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Base Unit Name (smallest)</label>
                <input type="text" value={baseUnitName} onChange={(e) => setBaseUnitName(e.target.value)}
                  placeholder="e.g. Cigarette, Piece, Biscuit"
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
              </div>
              {packagingMode === 'MULTI' && summaryLines.length > 0 && (
                <div className="flex items-end">
                  <div className="p-2 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                    {summaryLines.map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                </div>
              )}
            </div>

            {packagingMode === 'MULTI' && (
              <>
                <div className="space-y-3">
                  {levels.map((lv, idx) => {
                    const prevUnitFallback = idx === 0 ? (baseUnitName.trim() || 'Piece') : (levels[idx - 1].name || (baseUnitName.trim() || 'Piece'));
                    const matchedContainer = containerOptions.find((o) => o.name.toLowerCase() === String(lv.contains || '').toLowerCase());
                    const selectedContains = matchedContainer ? matchedContainer.name : (lv.contains || prevUnitFallback);
                    return (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-end gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex-1">
                          <label className="text-[10px] font-medium text-gray-400 block mb-0.5">Unit Name</label>
                          <input type="text" value={lv.name} onChange={(e) => updateLevel(idx, 'name', e.target.value)}
                            placeholder="e.g. Dibi, Pack, Box"
                            className="w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
                        </div>
                        <div className="w-20">
                          <label className="text-[10px] font-medium text-gray-400 block mb-0.5">Contains</label>
                          <input type="number" min="1" value={lv.quantity} onChange={(e) => updateLevel(idx, 'quantity', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
                        </div>
                        <div className="w-28">
                          <label className="text-[10px] font-medium text-gray-400 block mb-0.5">of (unit)</label>
                          <select
                            value={selectedContains}
                            onChange={(e) => updateLevel(idx, 'contains', e.target.value)}
                            className="w-full px-1.5 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
                          >
                            {containerOptions.map((o) => (
                              <option key={o.name} value={o.name}>{o.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="w-28">
                          <label className="text-[10px] font-medium text-gray-400 block mb-0.5">Plural Name</label>
                          <input type="text" value={lv.pluralName || ''} onChange={(e) => updateLevel(idx, 'pluralName', e.target.value)}
                            placeholder="auto" className="w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={!!lv.sellable} onChange={(e) => updateLevel(idx, 'sellable', e.target.checked)}
                              className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600" />
                            Sell?
                          </label>
                        </div>
                        {lv.sellable && (
                          <>
                            <div className="w-24">
                              <label className="text-[10px] font-medium text-gray-400 block mb-0.5">Sale / {lv.name || 'unit'}</label>
                              <input type="number" step="0.01" min="0" value={lv.salePrice} onChange={(e) => updateLevel(idx, 'salePrice', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
                            </div>
                            <div className="w-24">
                              <label className="text-[10px] font-medium text-gray-400 block mb-0.5">Cost / {lv.name || 'unit'}</label>
                              <input type="number" step="0.01" min="0" value={lv.purchasePrice} onChange={(e) => updateLevel(idx, 'purchasePrice', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
                            </div>
                          </>
                        )}
                        {levels.length > 1 && (
                          <button type="button" onClick={() => removeLevel(idx)}
                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button type="button" onClick={addLevel}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors">
                  <Plus size={13} /> Add Packaging Level
                </button>
              </>
            )}
          </div>
        </div>

        {/* min stock */}
        <div>
          <label className={labelClass}>Minimum Stock ({baseUnitName.trim() || 'Piece'}s)</label>
          <div className="relative">
            <Box size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="number" min="0" value={minStock} onChange={(e) => setMinStock(e.target.value)} placeholder="0" className={inputClass('minStock')} />
          </div>
        </div>

        {/* expiry */}
        <div>
          <label className={labelClass}>Expiry Date</label>
          <div className="relative">
            <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={inputClass('expiryDate')} />
          </div>
        </div>

        {/* initial stock (create only) */}
        {!isEdit && (
          <div className="md:col-span-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3">
            <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Initial Stock</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Unit</label>
                <select value={stockUnit} onChange={(e) => setStockUnit(e.target.value)} className={plainInputClass()}>
                  {units.map((u, i) => (
                    <option key={i} value={String(i)}>
                      {u.name}{u.baseFactor > 1 ? ` (${u.baseFactor} ${units[0].name}${u.baseFactor !== 1 ? 's' : ''})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Quantity *</label>
                <div className="relative">
                  <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="number" min="0" value={stockQty} onChange={(e) => setStockQty(e.target.value)} placeholder="0" className={inputClass('stockQty')} />
                </div>
                {errors.stockQty && <p className="mt-1 text-xs text-red-500">{errors.stockQty}</p>}
              </div>
              <div className="flex items-end">
                {initialStockPreview && (
                  <p className="text-xs text-blue-700 dark:text-blue-300 py-2.5">{initialStockPreview}</p>
                )}
              </div>
            </div>
          </div>
        )}
        {isEdit && (
          <div className="md:col-span-2">
            <label className={labelClass}>Current Stock ({baseUnitName.trim() || 'Piece'}s)</label>
            <input type="text" disabled
              value={`${product?.currentStock ?? product?.stock ?? product?.quantity ?? 0}`}
              className="w-full px-3 py-2.5 text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-400 cursor-not-allowed" />
          </div>
        )}

        {/* profit margin */}
        {(Number(purchasePrice) > 0 && Number(salePrice) > 0) && (
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Star size={15} className="text-amber-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Profit Margin:</span>
              {(() => {
                const margin = ((Number(salePrice) - Number(purchasePrice)) / Number(salePrice)) * 100;
                return (
                  <span className={`text-sm font-bold ${margin >= 30 ? 'text-emerald-600' : margin >= 15 ? 'text-amber-500' : 'text-red-500'}`}>
                    {margin.toFixed(1)}%
                  </span>
                );
              })()}
              <span className="text-xs text-gray-400 dark:text-gray-500">
                (Cost: Rs. {Number(purchasePrice).toLocaleString()} | Sale: Rs. {Number(salePrice).toLocaleString()})
              </span>
            </div>
          </div>
        )}

        {/* supplier */}
        <div className="md:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Supplier Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Supplier name" className={inputClass('supplierName')} />
            </div>
            <div className="relative">
              <Box size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={supplierContact} onChange={(e) => setSupplierContact(e.target.value)} placeholder="Supplier contact" className={inputClass('supplierContact')} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
            <Ban size={16} /> Cancel
          </button>
        )}
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          type="submit" disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
          {isEdit ? 'Update Product' : 'Save Product'}
        </motion.button>
      </div>
    </motion.form>
  );
}